import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { Fragment, useEffect, useMemo, useState } from 'react';

import PortalDepositModal from '@/components/modal/PortalDepositModal';
import { PortalSSIModal } from '@/components/modal/PortalSSIModal';
import PortalDepositTable from '@/components/portal-modules/PortalDepositTable';
import { formatToDate, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { DepositWithdrawalRequestHistoryReturn } from '@/types/sso';
import { TableFilter } from '@/types/table';
import Unauthorized401 from '@/Unauthorized401';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { fetchOnce, fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { subWeeks } from 'date-fns';
import { IconType } from 'react-icons/lib';
import { MdOutlineAccountBalance, MdOutlineAccountBalanceWallet, MdOutlineLayers } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';

const cardHeight = 120;
const tableHeight = 204.2;

export type RequestFilter = 'All' | 'DEPOSIT' | 'WITHDRAWAL' | 'INTERNAL_TRANSFER';
export type RequestStatusFilter = 'All' | 'Processing' | 'Completed' | 'Rejected' | 'Canceled';

export interface DepositWithdrawalRequestFilters {
    requestStatus: TableFilter<RequestStatusFilter>;
    startDate: TableFilter<Date>;
    endDate: TableFilter<Date>;
}

export interface InternalTransferFilters {
    startDate: TableFilter<Date>;
    endDate: TableFilter<Date>;
}

const currentTradeDate = getOperationalCurrentTradeDate();
function PortalDeposit() {
    usePortalAuthentication();

    const query = useSearchParams();
    const { jwt, foundEntity, hasPermission, onFetchJWT } = useSso();

    const lang = useLanguage();
    const screen = useScreenSizes();
    const credentials = useAppSelector(selectCredentials);
    const pagination = useAppSelector(selectTablePagination);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const portalDepositDisclosure = useDisclosure(false);
    const portalSSIDisclosure = useDisclosure(false);

    const [depositMode, setDepositMode] = useState<'bank' | 'crypto'>('bank');
    const [depositCcy, setDepositCcy] = useState('');
    const [depositPagination, setDepositPagination] = useState({ pageSize: pagination.pageSize });

    const defaultFilters = useMemo(
        () => ({
            requestStatus: {
                value: 'All' as const,
                label: lang.common.requestStatus
            },
            startDate: {
                value: subWeeks(currentTradeDate, 1),
                label: lang.common.startDate
            },
            endDate: {
                value: currentTradeDate,
                label: lang.common.endDate
            }
        }),
        [lang, currentTradeDate]
    );

    const [offset, setOffset] = useState<number>(0);
    const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
    const [filters, setFilters] = useState<DepositWithdrawalRequestFilters>(defaultFilters);

    const requestPayload = useMemo(
        () => ({
            entityId: foundEntity?.entityId.toString(),
            fromDate: formatToDate(filters.startDate.value),
            toDate: formatToDate(filters.endDate.value),
            type: 'Deposit,Internal-Transfer-Deposit',
            limit: depositPagination.pageSize,
            offset,
            status: filters.requestStatus.value !== 'All' ? filters.requestStatus.value : undefined
        }),
        [foundEntity?.entityId, depositPagination.pageSize, offset, filters]
    );

    const depositsApi = usePortalSWR<DepositWithdrawalRequestHistoryReturn | undefined>(
        '/api/requests/history-deposit',
        () => fetchPortalAPI(onFetchJWT, '/api/requests/history', jwt, requestPayload, 'POST'),
        fetchOnce
    );

    const { data: depositRequests } = depositsApi;

    useEffect(() => {
        depositsApi.mutate();
    }, [filters.requestStatus, filters.startDate, filters.endDate, offset, depositPagination.pageSize]);

    useEffect(() => {
        depositsApi.mutate().then(() => {
            setIsInitialMount(false);
        });
    }, [currentAccount]);

    useEffect(() => {
        const currency = query[0].get('currency');
        if (currency) {
            const config = getCurrency(currency);
            portalDepositDisclosure[1].open();
            setDepositMode(config.type === 'Crypto' ? 'crypto' : 'bank');
            setDepositCcy(config.show);
        }
    }, []);

    const isAuthorized = hasPermission('REQUEST_DEPOSIT');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <Fragment>
                    <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0">
                        <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                            <div className="font-bold text-xl">{lang.navigation.deposits}</div>
                        </div>
                        {isInitialMount && (depositsApi.isLoading || depositsApi.isValidating) ? (
                            <Fragment>
                                {screen.mobile ? (
                                    <div className="flex flex-col w-full gap-2 sm:gap-4">
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                        <div className="flex w-full relative">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                            <div
                                                className="absolute top-[57px] left-[25px] flex flex-wrap max-w-screen-lg gap-2 sm:gap-4"
                                                style={{
                                                    width: `calc(100% - 50px)`
                                                }}>
                                                <Skeleton containerClassName="flex-1" height={cardHeight} />
                                                <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            </div>
                                        </div>
                                        <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div>
                                    </div>
                                )}
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className="flex flex-col gap-4 bg-brand-background-dark p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-text rounded-md">
                                    <div className="">{lang.portal.makeADeposit}</div>
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4 max-w-screen-lg">
                                        <DepositWithdrawalCard
                                            Icon={MdOutlineAccountBalance}
                                            title={lang.portal.bankTransfer}
                                            description={lang.portal.bankTransferDescription}
                                            onClick={() => {
                                                portalDepositDisclosure[1].open();
                                                setDepositMode('bank');
                                            }}
                                            disabled={!hasPermission('REQUEST_DEPOSIT')}
                                        />
                                        {foundEntity?.attributes?.includes('DELIVERABLE') && (
                                            <DepositWithdrawalCard
                                                Icon={MdOutlineAccountBalanceWallet}
                                                title={lang.portal.cryptoTransfer}
                                                description={lang.portal.cryptoTransferDescription}
                                                onClick={() => {
                                                    portalDepositDisclosure[1].open();
                                                    setDepositMode('crypto');
                                                }}
                                                disabled={!hasPermission('REQUEST_DEPOSIT')}
                                            />
                                        )}
                                        <DepositWithdrawalCard
                                            Icon={MdOutlineLayers}
                                            title={lang.portal.standardSettlementInstructions}
                                            description={lang.portal.standardSettlementInstructionsDescription}
                                            onClick={portalSSIDisclosure[1].open}
                                            disabled={!hasPermission('REQUEST_DEPOSIT')}
                                        />
                                    </div>
                                </div>
                                <PortalDepositTable
                                    data={depositRequests}
                                    filters={filters}
                                    pagination={depositPagination}
                                    setPagination={setDepositPagination}
                                    setFilters={setFilters}
                                    setOffset={setOffset}
                                    defaultFilters={defaultFilters}
                                    isLoading={depositsApi.isLoading || depositsApi.isValidating}
                                    mutate={depositsApi.mutate}
                                />
                            </Fragment>
                        )}
                    </div>
                    <PortalDepositModal
                        mode={depositMode}
                        currency={depositCcy}
                        opened={portalDepositDisclosure[0]}
                        handlers={portalDepositDisclosure[1]}
                        onRefresh={depositsApi.mutate}
                    />
                    <PortalSSIModal opened={portalSSIDisclosure[0]} handlers={portalSSIDisclosure[1]} />
                </Fragment>
            )}
            {!isAuthorized && <Unauthorized401 />}
        </PortalLayout>
    ) : null;
}

export default PortalDeposit;

interface DepositWithdrawalCardProps {
    Icon: IconType;
    title: string;
    className?: string;
    description?: string;
    disabled?: boolean;
    onClick?: () => void;
}

export const DepositWithdrawalCard = (props: DepositWithdrawalCardProps) => {
    const { Icon, title, className, description, disabled = false, onClick } = props;

    return (
        <div
            className={cn(
                'bg-brand-background-dark p-4 border border-brand-neutral-700 hover:border-brand-neutral-600 rounded-md w-full sm:flex-[1_0_45%] md:flex-[1_0_30%] group',
                {
                    'cursor-not-allowed': disabled,
                    'cursor-pointer': !disabled
                }
            )}
            style={{ minHeight: cardHeight + 2 }}
            onClick={() => {
                if (!disabled && onClick) {
                    onClick();
                }
            }}>
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 text-brand-text">
                    <span>
                        <Icon
                            className={cn(
                                'w-5 h-5',
                                {
                                    'text-brand-primary group-hover:text-brand-primary-light': !disabled,
                                    'text-brand-neutral-400': disabled
                                },
                                className
                            )}
                        />
                    </span>
                    <div
                        className={cn('text-sm', {
                            'group-hover:text-brand-primary-light': !disabled,
                            'text-brand-neutral-400': disabled
                        })}>
                        {title}
                    </div>
                </div>
                <div className="flex flex-col gap-0.5">
                    {description && <div className="text-xs text-brand-neutral-400">{description}</div>}
                </div>
            </div>
        </div>
    );
};
